/**
 * V2 载荷的契约守卫（P1-1.7）
 *
 * 这里做的全部是「这份载荷能不能渲染」，<b>不是</b>「这个用户属于哪种情况」——
 * 后者是服务端的判定（D12 不变式）。区分得很死是有原因的：契约校验失败要整页降级 V1，
 * 而"这个用户数据不够"是正常的 V2 形态（`stage=S1`、`score.value=null`），两者搞混就会
 * 把大量正常用户错报成故障。
 *
 * 本文件允许出现的比较只有两类：字符串相等（schemaVersion/mode 取值集合）与"键在不在"。
 * 不允许出现任何对 `features.*` / `score.value` 的大小比较（R16，1.8 的构建期 lint 会扫）。
 */

// 与服务端 UserHabitProfileResponse.SCHEMA_VERSION 同值。
// 刻意从 versionPolicy 取而不是这里再写一个字面量：外壳（选版本）与视图（校载荷）各持一份时，
// 升到 hh-v2.1 会出现"外壳放行、视图判非法"或反过来，两种都表现为整页静默回 V1，极难查。
import { SUPPORTED_SCHEMA_VERSION } from '../versionPolicy';

export const PROFILE_SCHEMA_VERSION = SUPPORTED_SCHEMA_VERSION;

/** `dimensions` 恒含这四个键（服务端契约），缺一个就是结构漂移 */
export const DIMENSION_KEYS = ['SPORT', 'SLEEP', 'DIET', 'MENTAL'];

export const MODES = ['FULL', 'PARTIAL', 'SINGLE', 'INSUFFICIENT'];

export const STAGES = ['S0', 'S1', 'S2', 'S3', 'S4', 'S5'];

/** V1 契约里 `schemaVersion` 现值；indicators 缺这个键说明上游是旧包，连 `observed` 都没有 */
export const INDICATORS_SCHEMA_VERSION = 'hh-1.1';

const FAIL = (reason, detail) => ({
  ok: false,
  reason,
  detail: detail || null,
});
const PASS = { ok: true, reason: null, detail: null };

/**
 * 判定载荷本身是否可渲染。
 *
 * @param {object} profile `/healthHabit/v2/profile` 的 data
 * @returns {{ok:boolean, reason:string|null, detail:string|null}}
 */
export function checkProfile(profile) {
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) {
    return FAIL('EMPTY_PAYLOAD');
  }
  if (profile.schemaVersion !== PROFILE_SCHEMA_VERSION) {
    // 这是唯一一处"前端能自己判、且必须判"的形状问题：版本号是字符串相等，不是大小比较，
    // 所以它不算判定（D12），而 v2.1 若在没对齐的情况下硬渲染，会把新语义画成旧语义。
    return FAIL('SCHEMA_UNSUPPORTED', String(profile.schemaVersion));
  }
  if (typeof profile.mode !== 'string' || MODES.indexOf(profile.mode) === -1) {
    return FAIL('UNKNOWN_MODE', String(profile.mode));
  }
  if (
    typeof profile.stage !== 'string' ||
    STAGES.indexOf(profile.stage) === -1
  ) {
    return FAIL('UNKNOWN_STAGE', String(profile.stage));
  }
  const dims = profile.dimensions;
  if (!dims || typeof dims !== 'object') {
    return FAIL('MISSING_DIMENSIONS');
  }
  const missing = DIMENSION_KEYS.filter(
    (k) => !dims[k] || typeof dims[k] !== 'object',
  );
  if (missing.length) {
    return FAIL('MISSING_DIMENSIONS', missing.join(','));
  }
  // 分数三态必须自洽：observed=false 时 value 必须是 null（出现 0 就是服务端把"没分"写成了"0 分"）
  const dishonest = DIMENSION_KEYS.filter((k) => {
    const s = dims[k].score;
    return (
      s && s.observed === false && s.value !== null && s.value !== undefined
    );
  });
  if (dishonest.length) {
    return FAIL('SCORE_ZERO_FOR_MISSING', dishonest.join(','));
  }
  if (
    profile.index &&
    profile.index.observed === false &&
    profile.index.value !== null &&
    profile.index.value !== undefined
  ) {
    return FAIL('SCORE_ZERO_FOR_MISSING', 'index');
  }
  return PASS;
}

/**
 * 两端点一致性守卫（D14）。V2 的判定与日粒度序列来自两个请求，漂了就等于用 A 窗口的结论
 * 去讲 B 窗口的图 —— 数都对、故事是错的，而且页面上一切正常。
 *
 * 三条断言，按"最容易破"的顺序排：
 * 1. `profile.periodStart/End` 必须等于<b>我们请求的</b>窗口（服务端只回显，不自己另算）；
 * 2. `profile.nDays === indicators.nDays`（后端只该有一份 nDays 实现）；
 * 3. indicators 也得带契约字段（旧包 ⇒ 没有 schemaVersion/nDays，observed 一并缺失）。
 *
 * @returns {{ok:boolean, reason:string|null, detail:string|null}}
 */
export function checkWindowConsistency({ profile, indicators, window }) {
  if (!window || !window.periodStart || !window.periodEnd) {
    return FAIL('WINDOW_UNRESOLVED');
  }
  if (!profile || !indicators) {
    return FAIL('PARTIAL_PAYLOAD');
  }
  if (indicators.schemaVersion !== INDICATORS_SCHEMA_VERSION) {
    return FAIL('INDICATORS_SCHEMA_MISSING', String(indicators.schemaVersion));
  }
  if (profile.periodStart !== window.periodStart) {
    return FAIL(
      'PERIOD_START_MISMATCH',
      `${profile.periodStart} vs ${window.periodStart}`,
    );
  }
  if (profile.periodEnd !== window.periodEnd) {
    return FAIL(
      'PERIOD_END_MISMATCH',
      `${profile.periodEnd} vs ${window.periodEnd}`,
    );
  }
  if (profile.nDays !== indicators.nDays) {
    return FAIL('NDAYS_MISMATCH', `${profile.nDays} vs ${indicators.nDays}`);
  }
  return PASS;
}

/**
 * 桶码归一：未知码不降级、不崩页，只收集起来给埋点。
 * 新码在服务端先上、前端后更是<b>常态</b>（两次发布不可能同时），所以"见到未知码就降级"
 * 会让每一次服务端加码都变成一次前端事故。
 */
export function partitionBuckets(buckets, knownCodes) {
  const list = Array.isArray(buckets) ? buckets : [];
  const known = list.filter((c) => knownCodes.indexOf(c) >= 0);
  const unknown = list.filter((c) => knownCodes.indexOf(c) === -1);
  return { known, unknown };
}

/** 页面副标题用的窗口描述；只用服务端回显，不重算 */
export function windowLabel(profile) {
  if (!profile) return '';
  const { periodStart, periodEnd, windowDays } = profile;
  if (!periodStart || !periodEnd) return '';
  return `${periodStart} ~ ${periodEnd}（${windowDays || '?'} 天）`;
}
